// EventsCalendar — Events calendar view for AI Builder Hub.
//
// Presents recurring events (office hours, community of practice) with
// their upcoming dates, time window, host, location, and notes.
//
// Matches the CalendarModal / EventWidget views in:
//   design/project/CitiDev Hub.html
//   design/project/scripts/pages.jsx (CalendarModal + EventWidget functions)
//
// Design rules (AI_LAB_DESIGN_PRINCIPLES.md):
// - 4px grid, semantic color tokens (--surface, --ink, --green, --blue, etc.)
// - Full interactive-state coverage (hover, focus-visible)
// - Light / dark mode via CSS var() tokens
// - Each event has a tone colour bar matching the design's .bar.green / .bar.blue
//
// The component is presentation-only; it fetches from the API on mount.
// No external calendar sync in v1 — events are curated by admins (PRD §4/§10).

import { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/api-client";
import { downloadICS } from "@/features/hub-feed/calendar";
import { useProfile } from "@/lib/useProfile";
import { AdminEventForm } from "./AdminEventForm";
import "./events-calendar.css";

// ---------------------------------------------------------------------------
// Types — mirror api/schemas/events.py (NOT hand-edited api-types.ts)
// ---------------------------------------------------------------------------

export interface EventItem {
  id: string;
  title: string;
  tone: string;
  cadence: string;
  time_start: string;
  time_end: string;
  recurrence_dates: string[];
  host_id: string | null;
  location: string | null;
  note: string | null;
}

export interface EmptyState {
  headline: string;
  body: string;
}

export interface EventListResponse {
  items: EventItem[];
  empty_state: EmptyState | null;
  total: number;
}

// ---------------------------------------------------------------------------
// API fetch
// ---------------------------------------------------------------------------

async function fetchEvents(): Promise<EventListResponse> {
  return apiClient<EventListResponse>("/api/v1/events");
}

// ---------------------------------------------------------------------------
// Date formatting helpers (mirrors design/project/scripts/pages.jsx)
// ---------------------------------------------------------------------------

const MON_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const DOW_FULL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatDate(dateStr: string): string {
  // Parse as local date (YYYY-MM-DD) to avoid TZ offset issues
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return `${DOW_FULL[dt.getDay()]}, ${MON_ABBR[dt.getMonth()]} ${dt.getDate()}`;
}

// Local "today" as YYYY-MM-DD; recurrence dates sort lexicographically so a
// string >= compare is a correct date compare.
function todayISO(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// The next occurrence on or after today (dates are sorted ascending and may
// include past dates). Returns undefined when every occurrence is in the past.
function nextOccurrence(dates: string[]): string | undefined {
  const today = todayISO();
  return dates.find((d) => d >= today);
}

// ---------------------------------------------------------------------------
// Calendar icon
// ---------------------------------------------------------------------------

function CalendarIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// ClockIcon
// ---------------------------------------------------------------------------

function ClockIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// LocationIcon
// ---------------------------------------------------------------------------

function LocationIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// EventCard — a single recurring event row
// ---------------------------------------------------------------------------

interface EventCardProps {
  event: EventItem;
}

function EventCard({ event }: EventCardProps) {
  return (
    <article
      className="events-calendar__card"
      data-testid="event-card"
      data-event-id={event.id}
      aria-label={event.title}
    >
      {/* Tone accent bar */}
      <div
        className={`events-calendar__tone-bar events-calendar__tone--${event.tone}`}
        data-testid="event-tone-bar"
        aria-hidden="true"
      />

      {/* Card body */}
      <div className="events-calendar__card-body">
        {/* Title row */}
        <h3 className="events-calendar__card-title">{event.title}</h3>

        {/* Cadence */}
        <p className="events-calendar__cadence">{event.cadence}</p>

        {/* Time window */}
        <div className="events-calendar__meta-row">
          <span className="events-calendar__meta-icon">
            <ClockIcon />
          </span>
          <span className="events-calendar__time">
            {event.time_start} – {event.time_end}
          </span>
        </div>

        {/* Location */}
        {event.location && (
          <div className="events-calendar__meta-row">
            <span className="events-calendar__meta-icon">
              <LocationIcon />
            </span>
            <span className="events-calendar__location">{event.location}</span>
          </div>
        )}

        {/* Recurrence dates */}
        <div
          className="events-calendar__dates"
          data-testid="event-dates"
          aria-label={`Upcoming dates for ${event.title}`}
        >
          {event.recurrence_dates.map((dateStr) => (
            <span
              key={dateStr}
              className="events-calendar__date-chip"
              aria-label={formatDate(dateStr)}
            >
              {formatDate(dateStr)}
            </span>
          ))}
        </div>

        {/* Note */}
        {event.note && (
          <p className="events-calendar__note">{event.note}</p>
        )}

        {/* Host */}
        {event.host_id && (
          <p className="events-calendar__host">
            Hosted by{" "}
            <span className="events-calendar__host-id">{event.host_id}</span>
          </p>
        )}

        {/* Add to calendar — downloads an .ics for the next upcoming occurrence
            so viewers can drop it into Outlook / Google / Apple Calendar. */}
        {(() => {
          const next = nextOccurrence(event.recurrence_dates);
          if (!next) return null;
          return (
            <button
              type="button"
              className="events-calendar__addcal"
              data-testid="event-add-to-calendar"
              onClick={() =>
                downloadICS({
                  title: event.title,
                  date: next,
                  time_start: event.time_start,
                  time_end: event.time_end,
                  location: event.location,
                  note: event.note,
                })
              }
            >
              <CalendarIcon /> Add to calendar
            </button>
          );
        })()}
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// EventsEmptyState — rendered when the API returns no events
// ---------------------------------------------------------------------------

interface EventsEmptyStateProps {
  emptyState: EmptyState | null;
}

function EventsEmptyState({ emptyState }: EventsEmptyStateProps) {
  const headline = emptyState?.headline ?? "No upcoming events"; // [STUB] fallback
  const body =
    emptyState?.body ??
    "Check back later, or ask an admin to schedule the next office hours or community-of-practice session."; // [STUB] fallback

  return (
    <div
      className="events-calendar__empty"
      data-testid="events-empty-state"
      role="status"
      aria-live="polite"
    >
      <div className="events-calendar__empty-icon" aria-hidden="true">
        {/* Calendar/no-events icon */}
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </div>
      <p className="events-calendar__empty-headline">{headline}</p>
      <p className="events-calendar__empty-body">{body}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EventsCalendar — main export
// ---------------------------------------------------------------------------

export function EventsCalendar() {
  const [data, setData] = useState<EventListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { isAdmin } = useProfile();
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchEvents()
      .then((result) => {
        setData(result);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="events-calendar" data-testid="events-calendar">
      {/* Page header */}
      <div className="events-calendar__head">
        <div className="events-calendar__head-icon" aria-hidden="true">
          <CalendarIcon />
        </div>
        <div className="events-calendar__head-text">
          <div className="events-calendar__eyebrow">Community sessions</div>
          <h1 className="events-calendar__title">Events calendar</h1>
          <p className="events-calendar__subtitle">
            Recurring office hours, community of practice, and special sessions
            — all in one place.
          </p>
        </div>

        {/* Admin-only: add a new event. UX gate only — the backend enforces
            admin on POST /api/v1/admin/events (403 otherwise). */}
        {isAdmin && (
          <button
            type="button"
            className="events-calendar__add-btn"
            data-testid="admin-add-event"
            aria-expanded={showForm}
            onClick={() => setShowForm((v) => !v)}
          >
            {showForm ? "Cancel" : "+ Add event"}
          </button>
        )}
      </div>

      {isAdmin && showForm && (
        <AdminEventForm
          onCreated={() => {
            setShowForm(false);
            load();
          }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Loading */}
      {loading && (
        <div className="events-calendar__loading" role="status" aria-live="polite">
          Loading events…
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="events-calendar__error" role="alert">
          <strong>Could not load events.</strong>
          <p>{error.message}</p>
        </div>
      )}

      {/* Content */}
      {data && !loading && (
        <>
          {data.items.length === 0 ? (
            <EventsEmptyState emptyState={data.empty_state} />
          ) : (
            <div
              className="events-calendar__list"
              data-testid="events-list"
              aria-label="Upcoming recurring events"
            >
              {data.items.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
